import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { slugIsReserved } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ R-38 · the workspace is the first segment of every address.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EVERY WORKSPACE-SCOPED ADDRESS IS BUILT HERE, AND A BARE `'/tasks'` IS A LINT ERROR.**
 * There were sixty absolute path literals across seventeen files before this: `navigate('/tasks')`,
 * `to="/projects"`, `` navigate(`/contracts/${code}`) ``. Prefixing them one at a time would have
 * worked exactly once — the next screen reintroduces the bug, and the bug is silent, because
 * `/tasks` under R-38 is not a 404. It matches `/:workspace` with `workspace = 'tasks'`, so the app
 * renders a workspace-not-found for a screen that exists. `eslint.config.mjs` bans the literal so
 * the compiler is what catches it rather than somebody's memory.
 *
 * ⚠️ **The signed-out screens are NOT scoped and must not be** — `/sign-in`, `/sign-up`,
 * `/invite/:token`, `/auth/*`, the password-reset pair — plus `/welcome`, `/suspended` and
 * `/specimen`. A person reaching those has no workspace to name. `RESERVED_WORKSPACE_SLUGS` in
 * `@forecastify/shared` is that list, and `routes.test.tsx` asserts the two agree, so a new root
 * route cannot ship without either joining the list or failing the build.
 */

/**
 * The route parameter's name, and the pattern `routes.tsx` registers.
 *
 * ⚠️ One constant rather than the string `':workspace'` in two files: `useParams()` reads the name
 * and the route table writes it, and a typo makes `useWorkspaceSlug()` return `undefined` on every
 * screen — which presents as "no workspace" rather than as a mistake in a path.
 */
export const WORKSPACE_PARAM = 'workspace';

/** The prefix a scoped route carries. */
export const WORKSPACE_PREFIX = `/:${WORKSPACE_PARAM}`;

/**
 * A route pattern for `routes.tsx`: `scoped('/tasks')` is `/:workspace/tasks`.
 *
 * ⚠️ **`'/'` becomes the prefix alone rather than `/:workspace/`**, because a trailing slash makes
 * `matchPath` and `location.pathname === route.path` disagree, and `app.tsx` records at length that
 * the equality version shipped once and broke every parameterised screen's sidebar highlight.
 */
export function scoped(path: string): string {
  return path === '/' ? WORKSPACE_PREFIX : `${WORKSPACE_PREFIX}${path}`;
}

/**
 * A concrete address inside a named workspace: `workspacePath('delivery', '/tasks')`.
 *
 * ⚠️ **The slug is not encoded, deliberately.** `0003`'s CHECK constrains it to
 * `[a-z0-9-]`, so there is nothing for `encodeURIComponent` to do — and running it anyway would
 * hide the one case worth failing loudly on: a slug that somehow is not URL-safe means the
 * constraint is gone.
 */
export function workspacePath(slug: string, path: string): string {
  return path === '/' ? `/${slug}` : `/${slug}${path}`;
}

/**
 * The workspace slug in the current address, or `null` on a route that has none.
 *
 * ⚠️⚠️ **FROM `useLocation()`, NOT `useParams()` — AND THE PARAMS VERSION SHIPPED FIRST AND WAS
 * WRONG.** `Chrome` builds the sidebar ABOVE its own `<Routes>`, which is deliberate (the header
 * slot has to survive a route change), and `useParams()` reads the router's MATCH — so inside
 * `Chrome` it is **always empty**, on every screen. Every sidebar link came out as `/tasks` rather
 * than `/delivery/tasks`, and because a bare `/tasks` is *rescued* by
 * `resolveWorkspaceAddress` the links still worked: one redirect per click, the address bar
 * flickering through a URL nobody wrote, and the reachability gate the only thing that could see
 * it. The catch-all made it worse — `path="*"` captures no `:workspace` at all — so a 404 page's
 * whole sidebar lost the prefix.
 *
 * The first path segment is the same fact without the match dependency, and it is available to any
 * component under the router.
 *
 * ⚠️ **A RESERVED word is `null`, not a workspace.** On `/sign-in` the first segment is `sign-in`,
 * and returning it would let a caller build `/sign-in/account`. `RESERVED_WORKSPACE_SLUGS` is
 * exactly the list of first segments that are routes rather than workspaces — the same list the
 * database refuses as a slug, which is what makes this check total rather than a guess.
 *
 * ⚠️ **`null` rather than `''`**, so a caller cannot build `//tasks` by interpolating an empty
 * string and get an address that looks nearly right.
 */
export function useWorkspaceSlug(): string | null {
  const { pathname } = useLocation();
  const first = pathname.split('/').find((segment) => segment !== '');

  if (first === undefined || slugIsReserved(first)) return null;
  return first;
}

/**
 * Build an address inside the workspace the person is currently in.
 *
 * ⚠️⚠️ **IT RETURNS THE PATH UNCHANGED WHEN THERE IS NO WORKSPACE IN THE ADDRESS, RATHER THAN
 * THROWING.** The alternative is a screen that renders on an unscoped route and crashes the moment
 * it builds a link — and the screens this is called from are the scoped ones, so the case is a
 * component rendered somewhere new rather than a state a person can reach. Degrading to the bare
 * path keeps the tree alive; the reachability gate is what catches the address being wrong.
 */
export function useWorkspacePath(): (path: string) => string {
  const slug = useWorkspaceSlug();
  return useCallback((path: string) => (slug === null ? path : workspacePath(slug, path)), [slug]);
}

/**
 * `navigate`, with the workspace prefix applied.
 *
 * ⚠️⚠️ **THIS IS WHAT THE SIXTY CALL SITES BECAME, AND THE SHAPE IS DELIBERATE.** It takes the same
 * workspace-relative path they already passed — `go('/tasks/HSO-1')` — so converting a screen is a
 * rename rather than a rewrite, and a reader comparing against the mockup still sees the address
 * the mockup draws. The prefix is applied in one function instead of being interpolated in sixty.
 *
 * ⚠️ `void`-returning, because every existing call site was `void navigate(...)`: react-router's
 * navigate returns a promise nobody awaited, and `@typescript-eslint/no-floating-promises` made
 * each of those sites carry a `void`. Swallowing it here removes sixty of them.
 */
export function useWorkspaceNavigate(): (
  path: string,
  options?: { replace?: boolean; state?: unknown },
) => void {
  const navigate = useNavigate();
  const at = useWorkspacePath();
  const location = useLocation();
  /* A ref, so the function stays stable across renders — sixty call sites hold it in deps. */
  const here = useRef(location);
  here.current = location;

  return useCallback(
    (path: string, options?: { replace?: boolean; state?: unknown }) => {
      void navigate(at(path), {
        ...options,
        state: withOrigin(here.current, options),
      });
    },
    [navigate, at],
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · where a page was opened from (R-61, amended).                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A CHAIN, NOT A STEP.** Every in-app push records the page it left — address, query and
 * hash, so the project's *Szerződések* tab comes back as that tab — together with THAT page's own
 * origin. Going up to an origin restores the origin's origin, so project → contract → line item →
 * up → up lands on the project tab rather than bouncing between the two detail pages.
 *
 * ⚠️ **A replace keeps the page's origin**, because it is the same visit under a better address
 * (`/invoices/<uuid>` corrected to its identifier). A link that is not a `go` — a sidebar row, a
 * pasted address, a reload of a fresh tab — has no origin, and the screen falls back to its place
 * in the hierarchy.
 */
export interface Origin {
  readonly path: string;
  readonly origin: Origin | null;
}

function originOf(state: unknown): Origin | null {
  if (typeof state !== 'object' || state === null || !('origin' in state)) return null;
  const origin = (state as { origin?: unknown }).origin;
  return typeof origin === 'object' &&
    origin !== null &&
    typeof (origin as Origin).path === 'string'
    ? (origin as Origin)
    : null;
}

function withOrigin(
  from: { pathname: string; search: string; hash: string; state: unknown },
  options?: { replace?: boolean; state?: unknown },
): unknown {
  const extra = typeof options?.state === 'object' && options.state !== null ? options.state : {};
  /* A caller that names the origin itself (going up) has the last word. */
  if ('origin' in extra) return extra;
  const origin = options?.replace
    ? originOf(from.state)
    : { path: `${from.pathname}${from.search}${from.hash}`, origin: originOf(from.state) };
  return { ...extra, origin };
}

/**
 * A page header's back arrow: go UP to the screen's fixed parent — `parent`, a workspace-relative
 * path like the ones `go` takes. A report goes to the report list, a task to the task list, a line
 * item to its contract.
 *
 * ⚠️⚠️ **UP, NOT BACK — the product owner, 3 Nov 2026 (`decisions.md` R-61), overruling the
 * 14 Oct "history first" ruling.** The arrow used to step history, so it led wherever the person
 * happened to have been: switching between two pinned projects and pressing it landed on the
 * other project rather than on the list, and on a page opened from a sidebar row it walked back
 * through every screen glanced at on the way. An arrow in the header now means *one level up*,
 * always the same place from the same page; the browser's own Back is still there for history.
 *
 * ⚠️ **It PUSHES.** The parent is a new place, and the browser's Back from it should return to
 * the page just left — a `replace` would erase that page from the history.
 *
 * ⚠️ `file-route.tsx` does not use this: a file opens in a new tab, and its arrow closes back to
 * the tab it came from.
 */
export function useGoUp(): (parent: string, options?: { followOrigin?: boolean }) => void {
  const navigate = useNavigate();
  const at = useWorkspacePath();
  const location = useLocation();
  const here = useRef(location);
  here.current = location;

  return useCallback(
    (parent: string, options?: { followOrigin?: boolean }) => {
      const origin = originOf(here.current.state);
      const target = at(parent);
      /**
       * ⚠️⚠️ 3 Nov 2026 (R-61, amended) · **a contract, an invoice, a cost or a period goes back
       * to where it was opened from** — the project's *Szerződések* tab, the register, a search —
       * and to its place in the hierarchy only when nothing opened it (`followOrigin`). Every
       * other page keeps its fixed parent — except that an origin which IS the parent is restored
       * with its own origin, so a line item opened from its contract returns to that contract
       * still knowing where the contract was opened from.
       */
      const restores =
        origin !== null &&
        (options?.followOrigin === true || origin.path.split(/[?#]/u)[0] === target);
      if (restores) {
        void navigate(origin.path, { state: { origin: origin.origin } });
        return;
      }
      void navigate(target, { state: { origin: null } });
    },
    [navigate, at],
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ What an address says about which workspace somebody is in.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE URL IS THE SOURCE OF TRUTH AND `localStorage` IS THE FALLBACK, WHICH IS THE WHOLE
 * INVERSION R-38 ASKS FOR.** Before this the remembered id decided, and the address described
 * nothing: `/tasks/HSO-142` meant a different task depending on a value in another browser, two
 * tabs could not sit in two workspaces, and a link was not a link to what the sender saw. The
 * remembered id now answers one narrower question — *where do I go when the address does not say* —
 * which is `/`, a fresh sign-in, and a bookmark from before this change.
 */
export type WorkspaceAddress =
  /** An address with no workspace segment, and none expected: `/sign-in`, `/welcome`, `/specimen`. */
  | { readonly kind: 'root' }
  /** The address names a workspace this person belongs to. Adopt it. */
  | { readonly kind: 'enter'; readonly slug: string }
  /** Send them somewhere else, replacing history. */
  | { readonly kind: 'redirect'; readonly to: string }
  /**
   * The first segment is not a workspace this person is in.
   *
   * ⚠️⚠️ **AND IT DELIBERATELY CANNOT SAY WHETHER THE WORKSPACE EXISTS.** CLAUDE.md rule 5: a
   * record in another workspace is `not_found`, never `permission_denied`, because existence is
   * itself information. The client is in the same position — it knows only its own memberships —
   * so one outcome covers "no such workspace" and "not yours", and the screen says one thing.
   */
  | { readonly kind: 'unknown'; readonly slug: string };

/**
 * Everything the resolution needs, and nothing about React.
 *
 * ⚠️ An object rather than six positional parameters: `(pathname, slugs, fallback, home, roots,
 * scopedPaths)` is a signature whose arguments can be swapped without a type error, and two of them
 * are `readonly string[]`.
 */
export interface WorkspaceAddressInput {
  /** `location.pathname`, verbatim. */
  readonly pathname: string;
  /** Every workspace slug this person is a member of. */
  readonly slugs: readonly string[];
  /**
   * The workspace to assume when the address names none — the remembered one, or their first.
   * `null` when they have no membership at all.
   */
  readonly fallback: string | null;
  /** Where `/` goes inside a workspace: `HOME_PATH`. */
  readonly home: string;
  /** Route patterns that stay at the root, so no workspace segment is wanted. */
  readonly rootPaths: readonly string[];
  /**
   * Route patterns carrying `WORKSPACE_PREFIX`, used to tell a pre-R-38 bookmark (`/tasks`) apart
   * from a stranger's workspace (`/acme`).
   */
  readonly scopedPaths: readonly string[];
}

/**
 * Decide what an address means, given who the person is.
 *
 * ⚠️ **Pure, and it takes the route patterns rather than importing `ROUTES`.** `routes.tsx` imports
 * `scoped` from this file, so reaching back for the table would be a cycle — and a function that
 * takes its inputs is one a test can drive without a router.
 */
export function resolveWorkspaceAddress(input: WorkspaceAddressInput): WorkspaceAddress {
  const { pathname, slugs, fallback, home, rootPaths, scopedPaths } = input;

  /* ⚠️ A root route wins outright, and it has to be asked FIRST. `/welcome` would otherwise read as
     a workspace named `welcome` — which is exactly why `RESERVED_WORKSPACE_SLUGS` refuses that slug
     rather than leaving the ambiguity to be resolved by ordering. */
  if (rootPaths.some((pattern) => matchesPattern(pattern, pathname))) return { kind: 'root' };

  const first = pathname.split('/').find((segment) => segment !== '') ?? null;

  /* Bare `/`, or a path of nothing but slashes. */
  if (first === null) {
    return fallback === null
      ? { kind: 'root' }
      : { kind: 'redirect', to: workspacePath(fallback, home) };
  }

  if (slugs.includes(first)) return { kind: 'enter', slug: first };

  /**
   * ⚠️⚠️ **A PRE-R-38 BOOKMARK IS RESCUED RATHER THAN 404'd, AND THIS BRANCH IS WHY THE SCOPED
   * PATTERNS ARE PASSED IN.** `/tasks/HSO-142` was a real address for twenty chunks; it is now
   * `/{workspace}/tasks/HSO-142`. Without this, every link anybody saved lands on
   * "no such workspace" naming a workspace called `tasks` — which reads as the product having lost
   * their data rather than as an address having moved.
   *
   * ⚠️ It only fires when the whole remaining path matches a real screen, so `/acme` — a stranger's
   * workspace, matching no route — still falls through to `unknown` rather than being redirected
   * into the caller's own workspace and rendering somebody else's screen name.
   */
  if (fallback !== null) {
    const relative = scopedPaths.map(withoutWorkspacePrefix);
    if (relative.some((pattern) => matchesPattern(pattern, pathname))) {
      /**
       * ⚠️ **The trailing slash is dropped, and it is not cosmetic.** `/tasks/` matches `/tasks`
       * here — `matchesPattern` ignores empty segments — so a naive prefix would redirect to
       * `/delivery/tasks/`. react-router would still match the route, but `app.tsx` decides the
       * sidebar's `active` row by comparing `location.pathname` against the concrete address it
       * built, and `/delivery/tasks/` is not equal to `/delivery/tasks`. The row would go
       * unhighlighted on an address the app itself produced.
       */
      const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/u, '') : pathname;
      return { kind: 'redirect', to: workspacePath(fallback, trimmed) };
    }
  }

  return { kind: 'unknown', slug: first };
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 26 Oct 2026 · a workspace whose address changed while this tab was open.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Saving a new slug re-reads the session, and from then on the list holds the new slug only — so
 * every address this tab already has (the one on screen, every entry behind the Back button) names
 * a slug the list no longer contains, and `resolveWorkspaceAddress` would call it `unknown`.
 *
 * ⚠️⚠️ **THE ORDER CANNOT BE FIXED BY NAVIGATING AT THE RIGHT MOMENT.** React Router 7 applies a
 * navigation as a transition, so the re-read list renders first, under the old address — and
 * `WorkspaceScope` swapped the whole chrome for *"no such workspace"* for a frame, unmounting the
 * very screen that saved. Remembering what the slug WAS lets the old address keep meaning the same
 * workspace while the address bar is replaced.
 *
 * ⚠️ **This tab only, and only for as long as it lives.** Links from anywhere else are not
 * redirected — the product owner's call, 26 Oct 2026; `debt.md` keeps the history table that would.
 */

/** Old slug → current slug, after the workspace list went from `before` to `after`. */
export function renamedAfter(
  renamed: ReadonlyMap<string, string>,
  before: readonly { readonly id: string; readonly slug: string }[],
  after: readonly { readonly id: string; readonly slug: string }[],
): ReadonlyMap<string, string> {
  let next: Map<string, string> | null = null;
  for (const was of before) {
    const now = after.find((entry) => entry.id === was.id);
    if (now === undefined || now.slug === was.slug) continue;
    next ??= new Map(renamed);
    /* Renamed twice: what pointed at the middle slug points at the newest. */
    for (const [old, current] of next) if (current === was.slug) next.set(old, now.slug);
    next.set(was.slug, now.slug);
  }
  if (next === null) return renamed;
  /* ⚠️ A slug that belongs to a workspace again is that workspace's, not a memory of another. */
  for (const entry of after) next.delete(entry.slug);
  return next;
}

/**
 * `renamedAfter`, kept across the session's re-reads.
 *
 * ⚠️ State adjusted during render rather than a ref written there — the pattern React documents for
 * *"information from previous renders"*: the stale pass is discarded before anything commits.
 */
export function useRenamedSlugs(
  workspaces: readonly { readonly id: string; readonly slug: string }[],
): ReadonlyMap<string, string> {
  const [seen, setSeen] = useState(workspaces);
  const [renamed, setRenamed] = useState<ReadonlyMap<string, string>>(() => new Map());
  if (seen !== workspaces) {
    setSeen(workspaces);
    const next = renamedAfter(renamed, seen, workspaces);
    if (next !== renamed) setRenamed(next);
  }
  return renamed;
}

/** `/old/tasks/HSO-1` → `/new/tasks/HSO-1`: the first segment replaced, the rest untouched. */
export function withFirstSegment(pathname: string, slug: string): string {
  const match = /^\/[^/]+/u.exec(pathname);
  return match === null ? `/${slug}` : `/${slug}${pathname.slice(match[0].length)}`;
}

/** A route pattern with its `/:workspace` prefix removed: `/:workspace/tasks` → `/tasks`. */
export function withoutWorkspacePrefix(pattern: string): string {
  if (!pattern.startsWith(WORKSPACE_PREFIX)) return pattern;
  const rest = pattern.slice(WORKSPACE_PREFIX.length);
  return rest === '' ? '/' : rest;
}

/**
 * Whether a path matches a route pattern.
 *
 * ⚠️ **A hand-rolled match rather than `matchPath`, because this runs OUTSIDE a router.** The
 * resolution happens in `Gate` before any `<Route>` exists — `matchPath` itself is importable and
 * pure, but pulling react-router into a module a unit test drives with plain strings buys nothing.
 * The pattern language here is the one `routes.tsx` actually uses: literal segments and `:params`.
 */
function matchesPattern(pattern: string, pathname: string): boolean {
  const want = pattern.split('/').filter(Boolean);
  const have = pathname.split('/').filter(Boolean);
  if (want.length !== have.length) return false;

  return want.every((segment, index) =>
    /* ⚠️ A `:param` matches any ONE non-empty segment — never several, and never an empty one, or
       `/tasks/` would match `/tasks/:key` and render a task screen with no key. */
    segment.startsWith(':') ? (have[index] ?? '') !== '' : segment === have[index],
  );
}
